import {AfterContentInit, Component, EventEmitter, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {
  ApDynComponentComponent
}                                                                                from '../../../ap-dyncomponent/ap-dyncomponent.component';
import {
  ApDynGridColumnConfigBase
}                                                                                from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {combineLatest, Observable, Subscription}                                 from 'rxjs';
import {
  ApDynGridPagerConfigBase
}                                                                                from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {
  ApPermissions
}                                                                                from '../../../../../projects/invoker-transport/src/lib/generated/backend.enums';
import {
  ApDynGridStatusColumnConfig
}                                                                                from '../../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {
  ApDynGridPropertyColumnConfig
}                                                                                from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  ApDynGridPagerConfig
}                                                                                from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config';
import {
  GetPermissionPipe
}                                                                                from '../../../ap-permission/pipes/get-permission.pipe';
import {
  LanguageStore
}                                                                                from '../../../stores/translation/language.store';
import {
  LabResultAttachmentStore
}                                                                                from '../../../stores/dpl3/lab-result-attachment.store';
import {map}                                                                     from 'rxjs/operators';
import {
  ILabResultAttachmentGridItem
}                                                                                from '../../types/ap-lab-result-files-overview.types';
import {
  ApColumnState
}                                                                                from '../../../ap-interface/enums/ap-column-state.enum';
import {
  ApDynGridEditColumnConfig
}                                                                                from '../../../ap-dyngrids/config/ap-dyn-grid-edit-column-config';
import {
  ApUtilService
}                                                                                from '../../../ap-utils/service/ap-util.service';
import {
  FileService
}                                                                                from '../../../services/common/file.service';
import {saveAs}                                                                  from '@progress/kendo-file-saver';
import {
  NotifyStore
}                                                                                from '../../../stores/dialog/notify.store';
import {
  CampaignYearStore
}                                                                                from '../../../stores/login/campaignyear.store';
import {
  nameOf
}                                                                                from '../../../ap-core/utils/ap-name-of';
import {
  IGridStatus
}                                                                                from '../../../ap-interface/interfaces/ap-grid-status.interface';
import ILanguage = Data.Language.ILanguage;
import ILabResultAttachment = Data.Dpl3.ILabResultAttachment;
import ApValidationLevel = Data.Api.Validation.ApValidationLevel;
import IApValidationResult = Data.Api.Validation.IApValidationResult;

/**
 * Component for displaying and managing lab result files in a grid view.
 */
@Component({
  selector: 'ap-lab-result-files-overview',
  template: `
    <ap-dyncomponent [columns]="columns"
                     [items]="items$ | async"
                     [loading$]="loading$"
                     [pager]="pager"
                     [groupable]="true"
                     [canSearch]="true"
                     [selectable]="{checkboxOnly: false, mode: 'multiple'}"
    ></ap-dyncomponent>
  `
})
export class ApLabResultFilesOverviewComponent implements OnInit, AfterContentInit, OnDestroy {
  @ViewChild(ApDynComponentComponent, {static: true})
  public dynComponent: ApDynComponentComponent;
  public columns: ApDynGridColumnConfigBase[] = [];
  public pager: ApDynGridPagerConfigBase;
  public items$: Observable<ILabResultAttachmentGridItem[]>;
  public loading$: Observable<boolean> = this.labResultAttachmentStore.Loading$.asObservable();

  private _applyClicked = new EventEmitter<any>();
  private _subscriptions: Subscription[] = [];

  constructor(private notifyStore: NotifyStore,
              private languageStore: LanguageStore,
              private campaignYearStore: CampaignYearStore,
              private labResultAttachmentStore: LabResultAttachmentStore,
              private fileService: FileService,
              private permissionPipe: GetPermissionPipe) {
  }

  /**
   * Initializes component by setting up item stream and loading initial data
   * @public
   */
  public ngOnInit(): void {
    this.items$ = this.labResultAttachmentStore.LabResultAttachments$.pipe(
      map(labResultAttachment => this._generateGridItem(labResultAttachment))
    );
  }

  /**
   * Sets up component subscriptions after content initialization
   * Configures grid columns and pager based on permissions and language
   * @public
   */
  public ngAfterContentInit(): void {
    this._subscriptions.push(
      combineLatest([
        this.permissionPipe.transform(ApPermissions.SHOW_IMPORTS),
        this.permissionPipe.transform(ApPermissions.EDIT_IMPORTS),
        this.languageStore.SelectedLanguage$
      ]).subscribe(([_, isEditPermissions, selectedLanguage]) => {
        this.columns = this._generateColumns(selectedLanguage);
        this.pager = this._generatePager(isEditPermissions);
      })
    );
    this._subscriptions.push(
      this.campaignYearStore.SelectedCampaignYear$.subscribe(() => {
        this.labResultAttachmentStore.loadLabResultAttachments();
      })
    );
  }

  /**
   * Performs cleanup by unsubscribing from all subscriptions
   * @public
   */
  public ngOnDestroy(): void {
    this._subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  /**
   * Transforms lab result attachments into grid items
   * @param labResultAttachments - Array of lab result attachments
   * @returns Array of formatted grid items
   * @private
   */
  private _generateGridItem(labResultAttachments: ILabResultAttachment[]): ILabResultAttachmentGridItem[] {
    const gridItemStatus = {state: ApColumnState.Green, class: 'ap-status-green', description: ''};
    return labResultAttachments.map(labResultAttachment => {
      return {
        Id: labResultAttachment.Id,
        Status: gridItemStatus,
        Name: labResultAttachment.Name,
        Note: labResultAttachment.Note,
        LastModifiedDate: new Date(labResultAttachment.LastModified),
        ImportedDate: new Date(labResultAttachment.CreatedAt)
      } as ILabResultAttachmentGridItem;
    });
  }

  /**
   * Generates grid column configurations based on selected language
   * @param selectedLanguage - Current language settings
   * @returns Array of column configurations
   * @private
   */
  private _generateColumns(selectedLanguage: ILanguage): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridStatusColumnConfig({
        field: `${nameOf<ILabResultAttachmentGridItem>('Status')}.${nameOf<IGridStatus>('state')}`,
        class: `${nameOf<ILabResultAttachmentGridItem>('Status')}.${nameOf<IGridStatus>('class')}`,
        description: `${nameOf<ILabResultAttachmentGridItem>('Status')}.${nameOf<IGridStatus>('description')}`,
        title: 'Global__Status',
        groupTitle: 'Global__Status',
        showHeaderTitle: false
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<ILabResultAttachmentGridItem>('Name'),
        title: 'Global__File',
        filterable: true,
        width: 90,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<ILabResultAttachmentGridItem>('LastModifiedDate'),
        title: 'Global__CreatedAt',
        filterable: true,
        format: `${selectedLanguage.DateFormat} ${selectedLanguage.TimeFormat}`,
        width: 90,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<ILabResultAttachmentGridItem>('ImportedDate'),
        title: 'Base_Upload__Imported',
        filterable: true,
        format: selectedLanguage.DateFormat,
        width: 90,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<ILabResultAttachmentGridItem>('Note'),
        title: 'Global__Note',
        filterable: true,
        width: 90,
        headerFilterable: true
      }),
      new ApDynGridEditColumnConfig({
        data: [
          {
            text: 'Global__Download',
            callback: ApUtilService.createEventEmitter(this._downloadAttachment.bind(this), this._subscriptions)
          },
          {
            text: 'Global__Delete',
            callback: ApUtilService.createEventEmitter(this._deleteAttachment.bind(this), this._subscriptions)
          }
        ]
      })
    ];
  }

  /**
   * Generates pager configuration based on user permissions
   * @param isEditAvailableForUser - Flag indicating if user has edit permissions
   * @returns Pager configuration object
   * @remarks
   * Does not have any actions for pager
   * @private
   */
  private _generatePager(isEditAvailableForUser: boolean): ApDynGridPagerConfig {
    return new ApDynGridPagerConfig({
      listMassEditing: [],
      apply: this._applyClicked,
      hasPermission: isEditAvailableForUser
    });
  }

  /**
   * Handles attachment download requests
   * Downloads and saves the attachment file using the file service
   * @param dataItem - Grid item containing attachment information
   * @private
   */
  private _downloadAttachment(dataItem: ILabResultAttachmentGridItem): void {
    this.labResultAttachmentStore.downloadLabResultAttachment(dataItem.Id)
      .then(attachment => {
        const file = this.fileService.base64StringToFileData(attachment.Name,
          attachment.ContentMimeType, attachment.ContentBase64);
        saveAs(file, attachment.Name);
      })
      .catch(() => {
        this.notifyStore.addMessage({
          Counter: 1,
          DisplayTimeout: 5,
          ErrorKey: 'CannotDownloadFile',
          Level: ApValidationLevel.Error
        } as IApValidationResult);
      });
  }

  /**
   * Handles attachment deletion requests
   * @param dataItem - Grid item containing attachment information
   * @private
   */
  private _deleteAttachment(dataItem: ILabResultAttachmentGridItem): void {
    this.labResultAttachmentStore.deleteLabResultAttachment(dataItem.Id);
  }
}
